import { api } from "@/services";
import { API_ENDPOINTS } from "@/config";
import { formatRawTokenAmount } from "@/utils";

export interface APIEpochVotes {
    epoch: number;
    liquid: {
        total: string;
        votes: { farmAddress: string; votes: string }[];
    };
    illiquid: {
        total: string;
        votes: { farmAddress: string; votes: string }[];
    };
}

export interface APIResponse {
    code: string;
    data: APIEpochVotes;
}

export interface VotePercentage {
    farmAddress: string;
    percentage: number;
    votesCast: string;
}

export interface EpochVotesWithPercentages {
    epoch: number;
    liquid: {
        total: string;
        votes: VotePercentage[];
    };
    illiquid: {
        total: string;
        votes: VotePercentage[];
    };
}

const calculatePercentages = (votes: { farmAddress: string; votes: string }[], total: string): VotePercentage[] => {
    const totalBigInt = BigInt(total);
    if (totalBigInt === 0n) return [];

    return votes.map(({ farmAddress, votes }) => {
        const voteBigInt = BigInt(votes);
        const percentage = Number((voteBigInt * 10000n) / totalBigInt) / 100; // Multiply by 10000 and divide by 100 to get 2 decimal places
        return { farmAddress, percentage, votesCast: formatRawTokenAmount(votes, 18) };
    });
};

export const fetchGlobalVotes = async (epoch: number): Promise<EpochVotesWithPercentages> => {
    try {
        const targetEpoch = epoch;
        const response = await api.get<APIResponse>(
            API_ENDPOINTS.PROTOCOL.VOTES.GET_GLOBAL_VOTES_BY_EPOCH(targetEpoch),
        );
        const { data } = response.data;

        return {
            epoch: data.epoch,
            liquid: {
                total: data.liquid.total,
                votes: calculatePercentages(data.liquid.votes, data.liquid.total),
            },
            illiquid: {
                total: data.illiquid.total,
                votes: calculatePercentages(data.illiquid.votes, data.illiquid.total),
            },
        };
    } catch (error) {
        console.error("Error fetching global votes:", error);
        throw error;
    }
};

export interface APIUserVotes {
    epoch: number;
    liquid: {
        total: string;
        votes: { farmAddress: string; votes: string }[];
    };
    illiquid: {
        total: string;
        votes: { farmAddress: string; votes: string }[];
    };
    lockedTokenVotes: {
        [unwindingEpoch: string]: {
            liquid: { [farmAddress: string]: string };
            illiquid: { [farmAddress: string]: string };
        };
    };
}

export interface APIUserVotesResponse {
    code: string;
    data: APIUserVotes;
}

export interface UserVotesWithPercentages {
    epoch: number;
    liquid: {
        total: string;
        votes: VotePercentage[];
    };
    illiquid: {
        total: string;
        votes: VotePercentage[];
    };
    lockedTokenVotes: {
        [unwindingEpoch: string]: {
            liquid: VotePercentage[];
            illiquid: VotePercentage[];
        };
    };
    totalUserVotes: string;
}

export const fetchUserVotes = async (epoch: number, userAddress: string): Promise<UserVotesWithPercentages> => {
    try {
        const response = await api.get<APIUserVotesResponse>(
            API_ENDPOINTS.PROTOCOL.VOTES.GET_USER_VOTES(epoch, userAddress),
        );
        const { data } = response.data;

        // Convert lockedTokenVotes values to VotePercentage
        const convertedLockedTokenVotes = Object.entries(data.lockedTokenVotes).reduce(
            (acc, [unwindingEpoch, votes]) => {
                // Calculate totals for percentage calculation
                const liquidTotal = Object.values(votes.liquid).reduce((sum, amount) => sum + BigInt(amount), 0n);
                const illiquidTotal = Object.values(votes.illiquid).reduce((sum, amount) => sum + BigInt(amount), 0n);

                acc[unwindingEpoch] = {
                    liquid: Object.entries(votes.liquid).map(([farmAddress, amount]) => {
                        const voteBigInt = BigInt(amount);
                        const percentage = liquidTotal === 0n ? 0 : Number((voteBigInt * 10000n) / liquidTotal) / 100;
                        return {
                            farmAddress,
                            percentage,
                            votesCast: formatRawTokenAmount(amount, 16),
                        };
                    }),
                    illiquid: Object.entries(votes.illiquid).map(([farmAddress, amount]) => {
                        const voteBigInt = BigInt(amount);
                        const percentage =
                            illiquidTotal === 0n ? 0 : Number((voteBigInt * 10000n) / illiquidTotal) / 100;
                        return {
                            farmAddress,
                            percentage,
                            votesCast: formatRawTokenAmount(amount, 16),
                        };
                    }),
                };
                return acc;
            },
            {} as { [unwindingEpoch: string]: { liquid: VotePercentage[]; illiquid: VotePercentage[] } },
        );

        // Calculate total votes across all sources
        const allVotes = new Map<string, bigint>();

        // Add locked token votes
        for (const { liquid, illiquid } of Object.values(data.lockedTokenVotes)) {
            for (const [farmAddress, votes] of Object.entries(liquid)) {
                allVotes.set(farmAddress, (allVotes.get(farmAddress) || 0n) + BigInt(votes));
            }
            for (const [farmAddress, votes] of Object.entries(illiquid)) {
                allVotes.set(farmAddress, (allVotes.get(farmAddress) || 0n) + BigInt(votes));
            }
        }

        // Calculate total sum
        const totalSum = Array.from(allVotes.values()).reduce((sum, votes) => sum + votes, 0n);

        return {
            epoch: data.epoch,
            liquid: {
                total: data.liquid.total,
                votes: calculatePercentages(data.liquid.votes, data.liquid.total),
            },
            illiquid: {
                total: data.illiquid.total,
                votes: calculatePercentages(data.illiquid.votes, data.illiquid.total),
            },
            lockedTokenVotes: convertedLockedTokenVotes,
            totalUserVotes: totalSum.toString(),
        };
    } catch (error) {
        console.error("Error fetching user votes:", error);
        throw error;
    }
};
